"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTicketApi = void 0;
const react_1 = require("@reduxjs/toolkit/query/react");
/**
 * Ticket API factory — the platform (WebClient/MobileClient) supplies an authenticated
 * `baseQuery` at store-wiring time; this factory owns the reducer/cache/endpoints so both
 * platforms share one definition (contracts/shared-package-boundary.md). Typed against
 * `fetchBaseQuery`'s concrete return type (rather than the generic `BaseQueryFn`) so consumers
 * keep `FetchBaseQueryError`/`FetchBaseQueryMeta` inference instead of `unknown`.
 */
const createTicketApi = (baseQuery) => (0, react_1.createApi)({
    reducerPath: 'ticketApi',
    baseQuery,
    tagTypes: ['Ticket', 'TicketList', 'Document'],
    endpoints: (builder) => ({
        getTicketById: builder.query({
            query: (id) => ({
                url: 'Ticket',
                params: { request: id },
            }),
            providesTags: (_result, _error, id) => [{ type: 'Ticket', id }],
        }),
        getTicketsCreatedBy: builder.query({
            query: ({ customerName }) => ({
                url: 'TicketsCreatedBy',
                params: { customerName },
            }),
            providesTags: ['TicketList'],
        }),
        getTicketsAssignedTo: builder.query({
            query: ({ userName, email }) => ({
                url: 'TicketsAssignedTo',
                params: { userName, email },
            }),
            providesTags: ['TicketList'],
        }),
        getTicketsPendingCustomer: builder.query({
            query: ({ customerName }) => ({
                url: 'TicketsPendingCustomer',
                params: { customerName },
            }),
            providesTags: ['TicketList'],
        }),
        getTicketsAssignedToUserTeams: builder.query({
            query: ({ userId }) => ({
                url: 'TicketsAssignedToUserTeams',
                params: { userId },
            }),
            providesTags: ['TicketList'],
        }),
        listDocuments: builder.query({
            query: (ticketId) => ({
                url: `tickets/${ticketId}/documents`,
                method: 'GET',
            }),
            providesTags: (_result, _error, ticketId) => [
                { type: 'Document', id: ticketId },
                'Document',
            ],
        }),
        viewDocument: builder.query({
            query: ({ ticketId, documentId }) => ({
                url: `tickets/${ticketId}/documents/${documentId}/view`,
                method: 'GET',
                responseHandler: (response) => response.blob(),
            }),
        }),
        upsertTicket: builder.mutation({
            query: (body) => ({
                url: 'Ticket',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, arg) => [
                'TicketList',
                { type: 'Ticket', id: arg.updated.id || 'NEW' },
            ],
        }),
        uploadDocument: builder.mutation({
            query: ({ ticketId, file, fileName, documentType }) => {
                const formData = new FormData();
                // `fileName` is always passed explicitly as FormData's third argument. A browser
                // `File` already carries its own name, but mobile's Expo `expo-file-system` `File`
                // (T115) only implements `Blob`, not the DOM `File` interface, so FormData can't infer
                // a name from it on its own — passing it explicitly is correct and safe for both.
                formData.append('file', file, fileName);
                formData.append('documentType', documentType);
                return {
                    url: `tickets/${ticketId}/documents`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: 'Document', id: ticketId },
                'Document',
            ],
        }),
        downloadDocument: builder.mutation({
            query: ({ ticketId, documentId }) => ({
                url: `tickets/${ticketId}/documents/${documentId}/download`,
                method: 'GET',
                responseHandler: (response) => response.blob(),
            }),
        }),
        deleteDocument: builder.mutation({
            query: ({ ticketId, documentId }) => ({
                url: `tickets/${ticketId}/documents/${documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { ticketId }) => [
                { type: 'Document', id: ticketId },
                'Document',
            ],
        }),
    }),
});
exports.createTicketApi = createTicketApi;
